import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import { getOwner } from '@ember/application';
import RouterService from '@ember/routing/router-service';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import NewsletterModel from 'cio/models/newsletter';
import TagModel from 'cio/models/tag';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';
import { SerializedLegacyNewsletterRecipientFilter } from 'cio/types/conditions/legacy-filter';
import { isSerializedNewsletterRecipientCondition } from 'cio/utils/conditions/deserialize';
import DS from 'ember-data';
import { DEFAULT_NEWSLETTER_SETUP_STEPS } from 'cio/utils/newsletter-navigation';

export default class SetupController extends Controller {
  @service auth!: AuthService;
  @service api!: ApiService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  declare model: NewsletterModel;

  navSteps = DEFAULT_NEWSLETTER_SETUP_STEPS;

  @computed('findNewslettersUsedInPositiveConditionsTask.last.value')
  get conditionsIncludeDraftedNewsletters() {
    return (this.findNewslettersUsedInPositiveConditionsTask.last?.value ?? []).some((n) => !n.isSent);
  }

  @computed('router.currentRouteName', 'navSteps.@each.path')
  get currentNavStep() {
    return this.navSteps.find((step) => this.router.isActive(step.path));
  }

  @computed('currentNavStep', 'navSteps.[]')
  get nextNavStep() {
    let currentNavStepIndex = this.navSteps.indexOf(this.currentNavStep!);
    return this.navSteps[currentNavStepIndex + 1] || null;
  }

  @computed('currentNavStep')
  get currentNavStepController() {
    if (!this.currentNavStep) {
      return;
    }

    let controller = getOwner(this).lookup(`controller:${this.currentNavStep.path}.index`) as Controller &
      Record<string, any>;

    if (!controller?.model) {
      return;
    }

    return controller;
  }

  @computed('currentNavStepController.isDirty', 'currentNavStepController.isInvalid', 'auth.hasPermission')
  get isSaveable() {
    return (
      this.currentNavStepController?.isDirty &&
      !this.currentNavStepController?.isInvalid &&
      this.auth.hasPermission('journeys/newsletters/create')
    );
  }

  get canEditNewsletter() {
    return this.auth.hasPermission('journeys/newsletters/edit');
  }

  // we disable editing of a newsletter if user is redacted and the newsletter has an attribute filter
  get isRedactedUserWhoCannotEdit() {
    return this.auth.redacted && this.model.filters?.some((f) => f.type === 'attribute');
  }

  _saveNewsletter(attrs = {}) {
    let { model } = this;

    model.setProperties({
      ...attrs,
      updateType: 'main',
    });

    return model.save();
  }

  findNewslettersUsedInPositiveConditionsTask = task(async () => {
    let positiveNewsletterConditions = ((this.model as NewsletterModel).filters ?? []).filter(
      (c) => isSerializedNewsletterRecipientCondition(c) && c.newsletter_id && !c.negate,
    ) as SerializedLegacyNewsletterRecipientFilter[];

    return await Promise.all(
      positiveNewsletterConditions.map((c) => {
        // trying peekRecord first: will avoid redundant API calls in some cases
        // (it's impossible to use findRecord with background/optional reload, due to our Ember Data adapter setup)
        return (
          this.store.peekRecord('newsletter', c.newsletter_id) ?? this.store.findRecord('newsletter', c.newsletter_id)
        );
      }),
    );
  });

  saveAndNextTask = task(async () => {
    await this.currentNavStepController?.saveNewsletterTask.perform();

    this.router.transitionTo((this.nextNavStep as unknown as Record<string, any>).path, this.model.id);
  });

  @action
  addTag(tag: TagModel) {
    return this.model.addTags([tag]);
  }

  @action
  removeTag(tag: TagModel) {
    return this.model.removeTag(tag);
  }

  @action
  saveNewsletterName(name: string) {
    return this._saveNewsletter({ name });
  }

  @action
  saveNewsletter(attrs: Record<string, any>) {
    return this._saveNewsletter(attrs);
  }

  @action
  resetNewsletter() {
    return this.model.rollbackAttributes();
  }

  @action
  afterDeleteNewsletter() {
    return this.router.transitionTo('broadcasts');
  }
}

declare module '@ember/controller' {
  interface Registry {
    'broadcasts.newsletter.setup': SetupController;
  }
}
